// Uploads a generated Excel export into the RoboticImaging project's Photos
// files section (robotic-imaging-web's Files page), replicating that app's
// upload flow: bytes to Storage at project_files/{uuid}.{ext} with the
// original filename in metadata, then the projects-uploadProjectFilePostProcess
// callable records the file on the Firestore project document. One export per
// project: earlier survey exports found in the `pdf` (legacy target) or
// `photos` arrays are deleted first via projects-deleteProjectFile.
// The target project doc id is the property's ref_id.

import { getApps } from "@firebase/app";
import { getFunctions, httpsCallable } from "@firebase/functions";
import { getDownloadURL, getMetadata, ref, uploadBytes } from "@firebase/storage";
import { firebaseStorage, getFirebaseIdToken } from "@/shared/integrations/firebase/client";

/** Our deliverables are named "Store#_RI Prelim Survey_DG_MM.DD.YYYY.xlsm" —
 * the marker keeps the sweep away from other files in the section (e.g. the
 * generated "Property Report" PDFs). */
const EXPORT_FILENAME_RE = /RI Prelim Survey/i;

interface ProjectFileEntry {
  filename: string;
  filepath: string;
}

/**
 * The project's `pdf`/`photos` file arrays, read via Firestore REST — the app
 * has no Firestore SDK, and the ID token grants the same read the web app's
 * Files page performs. Returns empty arrays when the doc/fields are missing.
 */
async function fetchProjectFileArrays(
  projectId: string,
  refId: string,
  idToken: string
): Promise<Record<"pdf" | "photos", ProjectFileEntry[]>> {
  const url =
    `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/projects/${refId}` +
    `?mask.fieldPaths=pdf&mask.fieldPaths=photos`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
  if (!res.ok) throw new Error(`Failed to read project files (${res.status})`);
  const doc = (await res.json()) as {
    fields?: Record<string, { arrayValue?: { values?: Array<{ mapValue?: { fields?: Record<string, { stringValue?: string }> } }> } }>;
  };
  const entries = (key: "pdf" | "photos"): ProjectFileEntry[] =>
    (doc.fields?.[key]?.arrayValue?.values ?? [])
      .map((v) => ({
        filename: v.mapValue?.fields?.filename?.stringValue ?? "",
        filepath: v.mapValue?.fields?.filepath?.stringValue ?? "",
      }))
      .filter((f) => f.filepath);
  return { pdf: entries("pdf"), photos: entries("photos") };
}

export async function uploadExportToRIProject(
  file: Blob,
  filename: string,
  projectRefId: string
): Promise<string> {
  const app = getApps()[0];
  if (!app) throw new Error("Firebase is not initialized");
  const functions = getFunctions(app);

  // One export per project: sweep prior survey exports from both sections
  // before adding the new one. Best-effort — a stale record must not block
  // the new upload.
  try {
    const idToken = await getFirebaseIdToken();
    if (idToken && app.options.projectId) {
      const arrays = await fetchProjectFileArrays(app.options.projectId, projectRefId, idToken);
      for (const key of ["pdf", "photos"] as const) {
        const stale = arrays[key].filter((f) => EXPORT_FILENAME_RE.test(f.filename));
        for (const old of stale) {
          await httpsCallable(functions, "projects-deleteProjectFile")({
            projectId: projectRefId,
            deliverableKey: key,
            filepath: old.filepath,
          }).catch((err) => {
            console.warn(`[excel export] Could not remove previous export "${old.filename}" from ${key}:`, err);
          });
        }
      }
    }
  } catch (err) {
    console.warn("[excel export] Previous-export sweep failed; uploading anyway:", err);
  }

  const ext = filename.match(/\.([^.]+)$/)?.[1] ?? "xlsx";
  const filepath = `project_files/${crypto.randomUUID()}.${ext}`;
  const storageRef = ref(firebaseStorage, filepath);

  await uploadBytes(storageRef, file, {
    contentDisposition: `inline; filename="${filename}"`,
    customMetadata: { originalFilename: filename, projectId: projectRefId },
  });
  const [downloadUrl, meta] = await Promise.all([
    getDownloadURL(storageRef),
    getMetadata(storageRef),
  ]);

  await httpsCallable(functions, "projects-uploadProjectFilePostProcess")({
    projectId: projectRefId,
    deliverableField: "photos",
    filename,
    downloadUrl,
    filepath,
    fileSize: meta.size,
  });

  return downloadUrl;
}
